import { isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import Player from '@vimeo/player';
import type { VimeoUrl } from '@vimeo/player';

@Component({
	selector: 'app-vimeo-player',
	template: '<div #playerContainer class="vimeo-player__container"></div>',
	styles: [`
		:host {
			display: block;
			width: 100%;
		}

		.vimeo-player__container {
			width: 100%;
		}
	`],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class VimeoPlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
	@Input() videoUrl: VimeoUrl;
	@Output() videoPlay = new EventEmitter<void>();
	@Output() videoPause = new EventEmitter<void>();

	@ViewChild('playerContainer') private playerContainer: ElementRef<HTMLDivElement>;
	private player: Player | null = null;

	constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

	async ngAfterViewInit(): Promise<void> {
		if (isPlatformBrowser(this.platformId)) {
			await this.initPlayer();
		}
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (changes['videoUrl'] && !changes['videoUrl'].firstChange && this.player) {
			await this.player.loadVideo(this.videoUrl);
		}
	}

	private async initPlayer(): Promise<void> {
		if (!this.videoUrl || !this.playerContainer) {
			return;
		}
		if (this.player) {
			await this.player.destroy();
		}
		this.player = new Player(this.playerContainer.nativeElement, {
			url: this.videoUrl,
			responsive: true
		});
		this.player.on('play', () => this.videoPlay.emit());
		this.player.on('pause', () => this.videoPause.emit());
	}

	ngOnDestroy(): void {
		if (this.player) {
			this.player.destroy();
			this.player = null;
		}
	}
}
